import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Fetch } from '../../utils/Utils.js';
import AppDataTable from '../../shared/AppDataTable.js';
import { formatBalance, balanceClass } from '../currency.js';
import './Projects.css';

// ListProjects — Finance → Projects (Trello DdqnIHsy). Reads
// /api/finance/projects rather than the generic data endpoint, because each
// project's balance is summed server-side from its tagged transactions.
export const ListProjects = ({ currencySymbol }) => {
  const { t }    = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    Fetch('api/finance/projects', 'GET', null,
      (data) => setProjects(data || []),
      () => setProjects([])
    );
  }, []);

  const open = (p) => navigate(`/finances/projects/${p._id}`);
  const statusLabel = (p) => p.status === 'closed'
    ? <span className="project-status-closed">{t('project_status_closed')}</span>
    : t('project_status_active');

  if (projects === null) return <div className="project-empty">{t('loading')}…</div>;
  if (projects.length === 0) return <div className="project-empty">{t('no_projects')}</div>;

  if (isMobile) {
    return (
      <div className="project-card-list">
        {projects.map(p => (
          <div key={p._id} className="project-card" onClick={() => open(p)}>
            <div className="project-card-top">
              <span className="project-card-name">{p.name}</span>
              <span className={balanceClass(p.balance)}>{formatBalance(p.balance, currencySymbol)}</span>
            </div>
            <div className="project-card-meta">
              <span>{p.code}{p.matches ? ` · ${p.matches.split(';').join(', ')}` : ''}</span>
              <span>{statusLabel(p)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const columns = [
    {
      name: t('project_code'),
      selector: row => row.project_number,
      sortable: true,
      cell: row => <span className="wl-lot-link" onClick={() => open(row)}>{row.code}</span>,
    },
    { name: t('name'), selector: row => row.name, sortable: true },
    { name: t('nicknames'), selector: row => (row.matches || '').split(';').join(', ') || '—' },
    { name: t('status'), selector: row => row.status, sortable: true, cell: statusLabel },
    { name: t('transactions'), selector: row => row.transaction_count, sortable: true },
    {
      name: t('project_balance'),
      selector: row => row.balance,
      sortable: true,
      cell: row => <span className={balanceClass(row.balance)}>{formatBalance(row.balance, currencySymbol)}</span>,
    },
  ];

  return (
    <AppDataTable
      columns={columns}
      data={projects}
      keyField="_id"
      defaultSortFieldId={1}
      defaultSortAsc={false}
      pointerOnHover
      highlightOnHover
      onRowClicked={open}
    />
  );
};

export default ListProjects;
